import cacheSmartReplyDataAction from '../actions/cacheSmartReplyDataAction';
import { ClientItem, SmartReplySuggestion, SmartReplySuggestionType } from 'owa-mail-store';
import ExtendedPropertyType from 'owa-service/lib/contract/ExtendedPropertyType';
import { trace } from 'owa-trace';

// Used to reject any smart replies stamped before 20 June 2018.
// The backend team improved the model around that time and asked that we not show smart replies
// stamped using the older model.
const DATE_THRESHOLD = '2018-06-20T00:00:00.0000000Z';
const EXTRACTIONID_KEY = '@extractionTimeUtc';

const ENTITYID_KEY = '@EntityId';
const EXPECTED_NUM_SMART_REPLIES = 3;

const SMART_REPLY_SIMPLE_SUGGESTION = 'SmartReplyForEmail';
const SMART_REPLY_GROUP_SUGGESTION = 'SmartReplyForEmail_Group';

export default function parseSmartReplyProperty(
    item: ClientItem,
    extendedProperty: ExtendedPropertyType
): void {
    let validProperty: boolean = false;

    if (item) {
        if (extendedProperty) {
            try {
                const parsedProperty = JSON.parse(extendedProperty.Value)[0];
                const extractionTime: string = parsedProperty[EXTRACTIONID_KEY];

                // Ignore smart replies that are too old.
                if (extractionTime) {
                    const extractionTimeDateInMilliseconds: number = Date.parse(extractionTime);
                    const dateThresholdInMilliseconds: number = Date.parse(DATE_THRESHOLD);

                    if (extractionTimeDateInMilliseconds >= dateThresholdInMilliseconds) {
                        validProperty = extractAndCacheSmartRepliesFromProperty(
                            item,
                            parsedProperty
                        );
                    }
                } else {
                    trace.warn(
                        '[getSmartRepliesForItem_parseSmartReplyProperty] Time stamp not found on property, aborting smart reply parsing.'
                    );
                    const error: Error = new Error('NoTimeStamp');
                    (error as { networkError?: boolean }).networkError = true;
                    throw error;
                }
            } catch (e) {
                trace.warn(
                    '[getSmartRepliesForItem_parseSmartReplyProperty] Malformed property, cannot parse. Error: ' +
                        e.message
                );

                (e as { networkError?: boolean }).networkError = true;
                throw e;
            }
        } else {
            trace.info('[getSmartRepliesForItem_parseSmartReplyProperty] No property found.');
        }

        // If the property is valid, extractAndCacheSmartRepliesFromProperty will have cached the data.
        // Otherwise, cache that there is no valid property on the item.
        if (!validProperty) {
            cacheSmartReplyDataAction(item, validProperty, null);
        }
    } else {
        trace.info('[getSmartRepliesForItem_parseSmartReplyProperty] No item.');
    }
}

/**
 * Exported for testing purposes only. Should not be used otherwise.
 * @param item Item associated with smart reply property.
 * @param parsedProperty Parsed property to extract data from.
 */
export function extractAndCacheSmartRepliesFromProperty(
    item: ClientItem,
    parsedProperty: any
): boolean {
    // Extraction ID
    const smartReplyExtractionID = parsedProperty[ENTITYID_KEY];
    const smartReplies: SmartReplySuggestion[] = [];
    const loggableSmartReplies: string[] = [];
    let validProperty: boolean = false;

    // Smart replies
    const numSmartReplies = parsedProperty.entities.length;
    for (let i = 0; i < numSmartReplies; i++) {
        const { smartReply, loggableSmartReply } = extractSmartReplySuggestion(
            parsedProperty.entities[i]
        );
        smartReplies.push(smartReply);
        loggableSmartReplies.push(loggableSmartReply);
    }

    // Initialize smart reply data if we have extraction ID and expected number of smart replies.
    if (smartReplyExtractionID && smartReplies.length == EXPECTED_NUM_SMART_REPLIES) {
        validProperty = true;
        cacheSmartReplyDataAction(
            item,
            validProperty,
            smartReplies,
            loggableSmartReplies,
            smartReplyExtractionID
        );
    } else {
        trace.info(
            '[getSmartRepliesForItem_extractAndCacheSmartRepliesFromProperty] Unexpected number of smart replies on item. Expected ' +
                EXPECTED_NUM_SMART_REPLIES +
                ' but found ' +
                smartReplies.length
        );
        const error: Error = new Error('NumberOfSmartRepliesIs' + smartReplies.length);
        (error as { networkError?: boolean }).networkError = true;
        throw error;
    }

    return validProperty;
}

export function extractSmartReplySuggestion(
    entity: any
): {
    smartReply: SmartReplySuggestion;
    loggableSmartReply: string;
} {
    let smartReply: SmartReplySuggestion = null;
    let loggableSmartReply: string = null;

    switch (entity['@type']) {
        case SMART_REPLY_SIMPLE_SUGGESTION:
            smartReply = {
                type: SmartReplySuggestionType.Simple,
                suggestions: entity.value,
            };
            loggableSmartReply = entity.valueRaw || entity.value;
            break;

        case SMART_REPLY_GROUP_SUGGESTION:
            smartReply = {
                type: SmartReplySuggestionType.Group,
                suggestions: entity.values,
            };
            loggableSmartReply = JSON.stringify(entity.values);
            break;

        default:
            trace.warn(
                '[getSmartRepliesForItem_parseSmartReplyProperty] Unexpected entity type. Expected ' +
                    SMART_REPLY_SIMPLE_SUGGESTION +
                    ' or ' +
                    SMART_REPLY_GROUP_SUGGESTION +
                    ' but found ' +
                    entity['@type']
            );
    }

    return { smartReply, loggableSmartReply };
}
